<template>
  <div class="page-admin-tenant">
    <div class="page-admin-tenant-card">
      <!-- Header -->
      <div class="page-admin-tenant-card__header">
        <BaseAvatar
          class="page-admin-tenant-card__avatar"
          :name="tenant.name"
          :imageUrl="tenant.imageUrl"
          color="#ade9ff"
          large
        />
        <div class="page-admin-tenant-card__info">
          <h2 class="page-admin-tenant-card__title">{{ tenant.name }}</h2>
          <div class="page-admin-tenant-card__subscription">
            <BaseTag :style="getSubscriptionTagStyle(subscriptionType)">
              {{ $t(`subscriptions.${subscriptionType}`) }}
            </BaseTag>
            <div v-if="trialOn">
              {{ $t('page.admin-user-profile.trial', { date: formatStandardDate(trialEndDate) }) }}
              <span v-if="daysBeforeTrialEnds && daysBeforeTrialEnds !== 0" :class="getColorClass(daysBeforeTrialEnds)">
                ({{ getDhm(daysBeforeTrialEnds).split(' ')[0] }})
              </span>
            </div>
          </div>
        </div>
        <BaseButton
          isSmall
          isSquare
          isGrey
          class="page-admin-tenant-card__action-button"
          @click.native="openEditNameDialog"
          v-if="isManager"
        >
          {{ $t('actions.edit') }} <BaseIcon name="edit-2" />
        </BaseButton>
      </div>

      <!-- Website -->
      <div class="page-admin-tenant-card__scoring-description" :class="{ disabled: !tenantHasTenderScoringAccess() }">
        <hr class="page-admin-tenant-card__separator" />

        <div class="page-admin-tenant-card__subtitle">
          <div
            class="page-admin-tenant-card__subtitle--p"
            v-tooltip="{
              content: this.tenantHasTenderScoringAccess() ? '' : this.$t('page.admin-tenant.access.scoring'),
              placement: 'top',
            }"
          >
            {{ $t('page.admin-tenant.website.title') }}
            <BaseIcon v-if="!tenantHasTenderScoringAccess()" name="lock" class="base-navigation-buttons__lock" />
          </div>
          <BaseButton
            v-if="isAdminTenant && tenantHasTenderScoringAccess()"
            isSmall
            isSquare
            isGrey
            class="page-admin-tenant-card__action-button"
            @click.native="openEditWebsiteDialog"
          >
            <BaseIcon name="plus" v-if="!hasWebsite" />
            {{ hasWebsite ? $t('actions.edit') : $t('actions.add') }}
            <BaseIcon name="edit-2" v-if="hasWebsite" />
          </BaseButton>
        </div>
        <p class="page-admin-tenant-card__subtitle--description">
          {{ $t('page.admin-tenant.website.description') }}
        </p>

        <div class="page-admin-tenant__website-actions">
          <div v-if="hasWebsite" class="page-admin-tenant__website-url">
            <BaseIcon name="link" class="mr-6 website-link-icon" />
            <a :href="tenant.websiteUrl" target="_blank" rel="noopener noreferrer">{{ tenant.websiteUrl }}</a>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="page-admin-tenant-card__scoring-description" :class="{ disabled: !tenantHasTenderScoringAccess() }">
        <hr class="page-admin-tenant-card__separator" />
        <TenantDescription />
      </div>

      <!-- Offices -->
      <div>
        <hr class="page-admin-tenant-card__separator" />
        <div class="page-admin-tenant-card__subtitle">
          <p
            class="page-admin-tenant-card__subtitle--p"
            v-tooltip="{ content: missingOpportunityModuleTootltip, placement: 'top' }"
            :class="{ disabled: !tenantHasOpportunitiesAccess() }"
          >
            {{ $t('page.admin-tenant.office') }}
          </p>
          <BaseButton
            isSmall
            isSquare
            isGrey
            class="page-admin-tenant-card__action-button"
            @click.native="openAddLocalisation"
            v-if="isManager && tenantHasOpportunitiesAccess()"
          >
            {{ $t('actions.add') }} <BaseIcon name="plus" />
          </BaseButton>
        </div>
        <p class="page-admin-tenant-card__subtitle--description" :class="{ disabled: !tenantHasOpportunitiesAccess() }">
          {{ $t('page.admin-tenant.office-text') }}
        </p>
        <div v-if="tenantLocations.length === 0" class="page-admin-tenant-card__empty">
          {{ $t('basic-words.none') }}
        </div>
        <ul v-else class="page-admin-tenant-card__offices" :class="{ disabled: !tenantHasOpportunitiesAccess() }">
          <li
            v-for="(location, index) in tenantLocations"
            :key="index"
            class="page-admin-tenant-card__office"
            :class="{ 'is-disabled': !isManager }"
            @click="isManager && openEditLocalisation(location)"
          >
            <div class="page-admin-tenant-card__office-info">
              <BaseIcon name="map-pin" />
              <strong>{{ location.locationLabel }}</strong>
              <p class="page-admin-tenant-card__address">
                {{ location.mapLocation.addressText }}
              </p>
            </div>
            <BaseMoreActions
              v-if="tenantHasOpportunitiesAccess() && isManager"
              :title="location.locationLabel"
              menu-class="page-admin-tenant__actions-menu"
              :actions="rowActions"
              @select="$event === 'edit' ? openEditLocalisation(location) : openDeleteLocalisation(location)"
            />
          </li>
        </ul>
      </div>

      <!-- Aliases -->
      <div v-if="tenantHasBusinessIntelligenceAccess()" ref="adminTenantNormalizedNames">
        <hr class="page-admin-tenant-card__separator" />
        <AdminTenantNormalizedNames />
      </div>
    </div>

    <EditTenantWebsite
      ref="editTenantWebsite"
      :existingWebsite="tenant.websiteUrl"
      @update-website="onUpdateWebsiteConfirmed"
    />
    <EditTenantName ref="editTenantName" @update-name="updateTenantName"></EditTenantName>
    <EditAddLocationItemDialog ref="editAddLocalisationDialog" :selectedLocation="selectedLocalisation" />
    <DeleteDialog ref="deleteLocalisationDialog" @execute="deleteLocalisation" :buttonText="$t('actions.delete')">
      <template slot="content">
        <div class="page-localisation__delete-localisation">
          {{ $t('page.admin-localisation.delete-localisation') }}
        </div>
        <div class="localisation-manager__delete">
          <strong v-if="selectedLocalisation">
            {{ $t('basic-words.address') }}: {{ selectedLocalisation.mapLocation.addressText }}
          </strong>
        </div>
      </template>
    </DeleteDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  tenantHasOpportunitiesAccess,
  tenantHasTenderScoringAccess,
  tenantHasBusinessIntelligenceAccess,
} from '@/util/module-access-util';
import BaseTag from '@/components/base/BaseTag.vue';
import { getSubscriptionTagStyle } from '@/util/tag-tones/subscription-tag';
import { getDhm, formatStandardDate } from '@/util/date-util';
import TenantDescription from '@/components/page-admin-tenants/TenantDescription.vue';
import EditTenantName from '@/components/page-admin-tenants/EditTenantName.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseAvatar from '@/components/base/BaseAvatar.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import EditAddLocationItemDialog from '@/components/layout/page-admin-location/EditAddLocationItemDialog.vue';
import DeleteDialog from '@/components/page-admin-keywords/DeleteDialog.vue';
import AdminTenantNormalizedNames from '@/components/page-admin-tenants/AdminTenantNormalizedNames.vue';
import EditTenantWebsite from '@/components/page-admin-tenants/EditTenantWebsite.vue';

import DateMixin from '@/mixins/date-mixin';

export default {
  name: 'admin-tenant',
  mixins: [DateMixin],
  components: {
    BaseMoreActions,
    BaseTag,
    EditTenantName,
    EditTenantWebsite,
    TenantDescription,
    BaseIcon,
    BaseAvatar,
    BaseButton,
    EditAddLocationItemDialog,
    DeleteDialog,
    AdminTenantNormalizedNames,
  },
  data() {
    return {
      selectedLocalisation: null,
    };
  },
  computed: {
    rowActions() {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    ...mapGetters('tenant', ['tenant', 'subscriptionType', 'trialOn', 'trialEndDate', 'daysBeforeTrialEnds']),
    ...mapGetters('auth', ['isManager', 'isAdminTenant']),
    ...mapGetters('tenantLocations', ['tenantLocations']),
    hasWebsite() {
      return !!this.tenant.websiteUrl;
    },
    missingOpportunityModuleTootltip() {
      return this.tenantHasOpportunitiesAccess()
        ? this.$t('page.admin-tenant.access.opportunity')
        : this.$t('page.admin-tenant.access.opportunity');
    },
  },
  methods: {
    ...mapActions('tenant', ['updateTenant', 'getTenantWebsite', 'updateTenantWebsite']),
    ...mapActions('tenantLocations', ['deleteTenantLocation']),
    tenantHasTenderScoringAccess,
    tenantHasOpportunitiesAccess,
    tenantHasBusinessIntelligenceAccess,
    formatStandardDate,
    getDhm,
    getSubscriptionTagStyle,
    async updateTenantName(payload) {
      const name = typeof payload === 'string' ? payload : payload && payload.name;

      if (!name || !name.trim()) {
        return;
      }

      try {
        await this.updateTenant({
          tenantId: this.tenant.id,
          tenant: { name: name.trim() },
        });
        this.$toast.success(this.$t('toast.success') || 'Nom mis à jour');
      } catch (e) {
        console.error('updateTenantName failed', e);
        this.$toast.error(this.$t('basic-words.error'));
      }
    },
    openEditNameDialog() {
      this.$refs.editTenantName.openDialog(this.tenant);
    },
    openEditWebsiteDialog() {
      this.$refs.editTenantWebsite.openDialog();
    },
    async onUpdateWebsiteConfirmed(url) {
      try {
        await this.updateTenantWebsite(url);
        await this.fetchTenantWebsite();

        this.$refs.editTenantWebsite.closeDialog();
        this.$toast.success(this.$t('toast.success'));
      } catch (e) {
        this.$toast.error(e);
      }
    },
    async fetchTenantWebsite() {
      try {
        await this.getTenantWebsite(this.tenant.id);
      } catch (error) {
        this.$toast.error(error);
      }
    },

    openAddLocalisation() {
      this.selectedLocalisation = null;
      this.$refs.editAddLocalisationDialog.openDialog(this.selectedLocalisation);
    },
    openDeleteLocalisation(item) {
      this.selectedLocalisation = item;
      this.$refs.deleteLocalisationDialog.openDialog();
    },
    deleteLocalisation() {
      if (this.selectedLocalisation && this.selectedLocalisation.id) {
        const locationId = this.selectedLocalisation.id;
        this.deleteTenantLocation(locationId)
          .then(() => {
            this.$toast.success(this.$t('page.admin-localisation.delete-success'));
          })
          .catch((error) => {
            this.$toast.error(error);
          });
      }
    },
    openEditLocalisation(item) {
      this.selectedLocalisation = item;
      this.$refs.editAddLocalisationDialog.openDialog(this.selectedLocalisation);
    },
  },
  mounted() {
    const hasOtherNamesHash = this.$route.hash !== '' && this.$route.hash === '#other-names';
    if (hasOtherNamesHash) {
      const anchor = this.$refs.adminTenantNormalizedNames;
      if (anchor) anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  },
  async created() {
    await this.fetchTenantWebsite();
  },
};
</script>

<style lang="scss">
.page-admin-tenant {
  padding: 32px;

  &__actions-menu {
    cursor: pointer;
    background-color: $color-white;
  }
  &-card {
    padding: 32px;
    background: $color-white;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
    max-width: 800px;
    min-height: 500px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      @include screen-below('tablet') {
        flex-wrap: wrap;
        gap: 8px;
      }

      .base-avatar {
        font-weight: bold;
      }
    }

    &__subscription {
      display: flex;
      font-size: 14px;
      color: $color-normal-pale;
      align-items: center;
      gap: 8px;
    }

    &__scoring-description {
      &.disabled {
        .page-admin-tenant-card__subtitle,
        .page-admin-tenant-card__subtitle--description {
          color: $color-normal-pale;
        }
      }
    }

    &__info {
      flex: 1;
      margin-left: 15px;
    }

    &__title {
      color: $color-normal;
      font-size: $font-size-xxbigger;

      @include screen-below('tablet') {
        font-size: 22px;
        line-height: 28px;
      }
    }

    &__action-button {
      margin-left: auto;

      // Phones: small to the eye, 44px to tap (also TenantDescription's).
      .base-button__element {
        @include phone-touch-area;
      }

      button {
        display: flex;
        gap: 4px;

        .base-icon {
          width: 14px;
        }
      }
    }

    &__subtitle {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      // Phones: the buttons go under the title when they don't fit beside it.
      @include screen-below('tablet') {
        flex-wrap: wrap;
        gap: 8px;
      }

      &--p {
        width: max-content;
        font-weight: bold;
        display: flex;
        gap: 8px;
        align-items: center;
      }

      &--description {
        margin-bottom: 10px;
        line-height: 1.5;
      }
    }

    &__separator {
      border: none;
      border-top: 1px solid $color-border;
      margin: 30px 0;
    }

    &__offices {
      list-style-type: none;
    }

    &__office {
      cursor: pointer;
      padding: 8px;
      margin-bottom: 8px;
      border-radius: $border-radius-medium;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .base-icon.feather.feather-more-horizontal {
        margin-left: 16px;
      }
      &:hover {
        background-color: $color-hover;
      }
    }
    &__office.is-disabled {
      pointer-events: none;
      cursor: default;
      :hover {
        background-color: $color-white;
      }
    }

    &__office-info {
      display: flex;
      align-items: center;
      width: 100%;

      .base-icon {
        margin-right: 6px;
        width: 18px;
      }
    }

    &__empty {
      color: $color-neutral;
    }

    &__address {
      margin-left: auto;
      text-align: right;
      max-width: 60%;
      color: $color-normal-pale;
    }

    // Phones: the address under the office's name, across the line, not squeezed beside it.
    @include screen-below('tablet') {
      &__office {
        align-items: center;
      }

      &__office-info {
        flex-wrap: wrap;
        row-gap: 2px;
      }

      &__address {
        flex-basis: 100%;
        max-width: none;
        margin: 0 0 0 24px;
        text-align: left;
      }
    }
  }

  .page-admin-tenant-card__actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__website-actions {
    margin: 8px 0 6px 0;
  }
  &__website-url {
    margin-top: 8px;
    display: flex;
    align-items: center;
    .base-icon {
      width: 14px;
      height: 14px;
    }
    a {
      text-decoration: underline;
      word-break: break-all;
    }
    .mr-6 {
      margin-right: 6px;
    }
  }
}

.website-link-icon {
  line-height: 0;
}

.nowrap {
  white-space: nowrap;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.modal-card {
  background: $color-white;
  border-radius: $border-radius-root;
  box-shadow: $shadow-default;
  width: 520px;
  max-width: calc(100% - 32px);
  padding: 16px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  h3 {
    margin: 0;
  }
  .modal-close {
    cursor: pointer;
  }
}

.modal-body {
  margin: 12px 0;

  .modal-label {
    display: block;
    font-weight: bold;
    margin-bottom: 6px;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;

  .mr-8 {
    margin-right: 8px;
  }
}

.page-admin-tenant-card__subtitle--p.disabled,
.page-admin-tenant-card__subtitle--description.disabled,
.page-admin-tenant-card__offices.disabled {
  color: $color-normal-pale;
}
.page-admin-tenant-card__offices.disabled {
  pointer-events: none;
  cursor: not-allowed;
}
</style>
